import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getChangesReports, exportReport } from '../../../models/reportsModel';

function ChangesReports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changes, setChanges] = useState([]);
  const [filters, setFilters] = useState({
    changeType: '',
    module: '',
    severity: '',
    user: '',
    status: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [contextModal, setContextModal] = useState({ isOpen: false, context: null, changeInfo: null });

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && user.user_id) {
      fetchChangesData(1);
    }
  }, [user]);

  useEffect(() => {
    // Only fetch changes if user is authenticated and we have filters
    if (user && user.user_id) {
      fetchChangesData(1);
    }
  }, [filters, user]);

  const fetchChangesData = async (page = 1) => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      const params = {
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...filters
      };

      const response = await getChangesReports(params);
      
      // Handle successful response
      if (response && (response.status === 1 || response.status === 'success')) {
        // Handle both old and new data formats
        const changesData = response.data?.changes || response.data || [];
        
        setChanges(changesData);
        setPagination(prev => ({
          ...prev,
          page: response.data?.page || page,
          total: response.data?.total || changesData.length,
          totalPages: Math.ceil((response.data?.total || changesData.length) / pagination.limit)
        }));
        
        // Log successful fetch for debugging
        console.log('Critical Changes Report: Fetched', changesData.length, 'changes from enterprise logging system');
      } else {
        // Handle API error response
        const errorMessage = response?.message || 'Failed to fetch changes reports';
        console.error('API returned error:', errorMessage);
        setError(errorMessage);
        
        // Set empty data to prevent UI crashes
        setChanges([]);
        setPagination(prev => ({
          ...prev,
          page: 1,
          total: 0,
          totalPages: 1
        }));
      }
    } catch (err) {
      console.error('Error fetching changes reports from enterprise logging system:', err);
      
      // Determine error message based on error type
      let errorMessage = 'Failed to fetch changes reports';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.name === 'SyntaxError') {
        errorMessage = 'Invalid response from server - please try again';
      } else if (err.name === 'TypeError') {
        errorMessage = 'Network error - please check your connection';
      }
      
      setError(errorMessage);
      
      // Set empty data to prevent UI crashes
      setChanges([]);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      changeType: '',
      module: '',
      severity: '',
      user: '',
      status: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };


  const handleExport = async () => {
    try {
      await exportReport('changes', filters);
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatContext = (context, changeInfo = null) => {
    if (!context) return <span className="text-xs text-gray-500">No context</span>;
    
    try {
      const parsed = typeof context === 'string' ? JSON.parse(context) : context;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        const totalItems = Object.keys(parsed).length;
        
        return (
          <div className="text-xs text-gray-600 max-w-xs">
            {Object.entries(parsed).slice(0, 3).map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="font-medium">{key}:</span> {String(value || '')}
              </div>
            ))}
            {totalItems > 3 && (
              <button
                onClick={() => setContextModal({ isOpen: true, context: parsed, changeInfo })}
                className="text-blue-500 hover:text-blue-700 cursor-pointer underline"
              >
                +{totalItems - 3} more...
              </button>
            )}
          </div>
        );
      }
      return <span className="text-xs text-gray-500">No context</span>;
    } catch (err) {
      console.error('Error formatting context:', err);
      return <span className="text-xs text-gray-500">Invalid context</span>;
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800',
      'info': 'bg-blue-100 text-blue-800',
      'emergency': 'bg-red-100 text-red-800',
      'major': 'bg-orange-100 text-orange-800',
      'minor': 'bg-yellow-100 text-yellow-800',
      'trivial': 'bg-green-100 text-green-800'
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getChangeTypeColor = (type) => {
    const colors = {
      'config': 'bg-blue-100 text-blue-800',
      'security': 'bg-red-100 text-red-800',
      'update': 'bg-green-100 text-green-800',
      'rollback': 'bg-orange-100 text-orange-800',
      'deployment': 'bg-purple-100 text-purple-800',
      'maintenance': 'bg-yellow-100 text-yellow-800',
      'emergency': 'bg-red-100 text-red-800',
      'patch': 'bg-indigo-100 text-indigo-800',
      'upgrade': 'bg-teal-100 text-teal-800'
    };
    return colors[type?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'implemented': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'rolled_back': 'bg-orange-100 text-orange-800',
      'completed': 'bg-emerald-100 text-emerald-800',
      'in_progress': 'bg-purple-100 text-purple-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'emergency':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'high':
      case 'major':
        return (
          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'medium':
      case 'minor':
        return (
          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getCriticalChangesToday = () => {
    const today = new Date().toDateString();
    return changes.filter(change => {
      const changeDate = new Date(change.created_at || change.change_date || change.timestamp).toDateString();
      return changeDate === today && 
             (change.severity === 'critical' || change.severity === 'emergency' || 
              change.impact_level === 'critical' || change.impact_level === 'emergency');
    }).length;
  };

  const getChangesThisWeek = () => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return changes.filter(change => 
      new Date(change.created_at || change.change_date || change.timestamp) >= weekAgo
    ).length;
  };

  const getFailedChanges = () => {
    return changes.filter(change => 
      change.status === 'failed' || change.status === 'rolled_back'
    ).length;
  };

  const getPendingApprovals = () => {
    return changes.filter(change => 
      change.status === 'pending' || change.status === 'awaiting_approval'
    ).length;
  };

  // Context Modal Component
  const ContextModal = () => {
    if (!contextModal.isOpen || !contextModal.context) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Change Context Details</h3>
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, changeInfo: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Change Info */}
            {contextModal.changeInfo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600">
                  <strong>Timestamp:</strong> {formatDate(contextModal.changeInfo.created_at || contextModal.changeInfo.timestamp)}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>Description:</strong> {contextModal.changeInfo.description || contextModal.changeInfo.change_description}
                </div>
              </div>
            )}

            {/* Context Data */}
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {Object.entries(contextModal.context).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-100 pb-2">
                    <div className="flex flex-col sm:flex-row">
                      <div className="w-full sm:w-1/3 font-medium text-gray-900 text-sm">
                        {key}:
                      </div>
                      <div className="w-full sm:w-2/3 text-sm text-gray-700 break-all">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, changeInfo: null })}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
             {/* Professional Header Card */}
             <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
               <div className="flex items-start justify-between">
                 {/* Left: Icon, Title, Description */}
                 <div className="flex items-start">
                   <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                        style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                     <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                             d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                     </svg>
                   </div>
                   <div>
                     <h1 className="text-xl font-bold text-gray-800">Critical Changes Reports</h1>
                     <p className="text-xs text-gray-500 mb-1">Monitor system changes and security modifications</p>
                     <div className="flex items-center mt-1">
                       <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                         {changes.length} change{changes.length !== 1 ? 's' : ''} total
                       </span>
                     </div>
                   </div>
                 </div>
                 
                 {/* Right: Controls and Actions */}
                 <div className="flex flex-col gap-2 items-end min-w-0">
                   <div className="flex items-center gap-2">
                     <button
                       onClick={() => {
                         setError(null);
                         fetchChangesData(1);
                       }}
                       className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                       style={{ 
                         backgroundColor: '#0087D4', 
                         boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                       }}
                     >
                       <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                       </svg>
                       Refresh
                     </button>
                   </div>
                 </div>
               </div>
             </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Change Report Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Change Type</label>
                <select
                  value={filters.changeType}
                  onChange={(e) => handleFilterChange('changeType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  <option value="config">Configuration</option>
                  <option value="security">Security</option>
                  <option value="update">Update</option>
                  <option value="rollback">Rollback</option>
                  <option value="deployment">Deployment</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="emergency">Emergency</option>
                  <option value="patch">Patch</option>
                  <option value="upgrade">Upgrade</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="info">Info</option>
                  <option value="emergency">Emergency</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                  <option value="trivial">Trivial</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search changes, modules, users..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Changes Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Enterprise Critical Changes</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading critical changes...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Changes</h3>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    fetchChangesData(1);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : changes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No critical changes found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Module
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Context
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {changes.map((change, index) => {
                      // Safety check for change object
                      if (!change || typeof change !== 'object') {
                        console.warn('Invalid change object at index', index, change);
                        return null;
                      }
                      
                      return (
                        <tr key={change.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {change.created_at || change.timestamp ? formatDate(change.created_at || change.timestamp) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(change.severity || change.impact_level)}`}>
                              {change.severity || change.impact_level || 'Medium'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getChangeTypeColor(change.change_type || change.type)}`}>
                              {change.change_type || change.type || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {change.module || change.affected_module || 'System'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {change.user || change.user_name || change.changed_by || 'System'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(change.status)}`}>
                              {change.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                            <div className="truncate" title={change.description || change.change_description || change.details || ''}>
                              {change.description || change.change_description || change.details || 'No description'}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            {formatContext(change.context, change)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => fetchChangesData(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchChangesData(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => fetchChangesData(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => fetchChangesData(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pagination.page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => fetchChangesData(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Context Modal */}
        <ContextModal />
      </div>
    </div>
  );
}

export default ChangesReports; 